/**
 * FeedbackDialog
 *
 * Modal that captures the reviewer's free-text resolution reason when they
 * accept / reject / edit / ignore a Coverage Audit issue. Presentation only —
 * the parent (`IssueCard`) owns the submission and store wiring.
 *
 * Built on the project's Radix-based `Dialog` primitive and shared `Button` /
 * `Textarea` components so it inherits the Management Console (purple) theme
 * and accessibility wiring (focus trap, ESC-to-close, ARIA labels).
 */
import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import type { IssueReviewAction } from '../../stores/coverageAuditStore';

const ACTION_LABEL: Record<IssueReviewAction, string> = {
  accepted: 'Accept (real failure)',
  rejected: 'Reject (detector wrong)',
  edited: 'Edit (partial)',
  ignored: 'Ignore (not actionable)',
};

const ACTION_DESCRIPTION: Record<IssueReviewAction, string> = {
  accepted:
    'Confirm the detector caught a genuine coverage gap. Briefly describe what the model missed.',
  rejected:
    'The detector flagged something that is not actually a gap. Briefly explain why.',
  edited:
    'The flag is partially correct. Describe what is right and what needs adjustment.',
  ignored:
    'The flag is technically correct but not worth acting on. Explain the rationale.',
};

const MIN_REASON_LENGTH = 5;

export interface FeedbackDialogProps {
  isOpen: boolean;
  action: IssueReviewAction | null;
  /** Submitter callback. The parent forwards this to the store. */
  onSubmit: (
    resolutionReason: string,
    correctedContent?: Record<string, unknown>,
  ) => Promise<void>;
  onClose: () => void;
}

export const FeedbackDialog: React.FC<FeedbackDialogProps> = ({
  isOpen,
  action,
  onSubmit,
  onClose,
}) => {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset local state whenever the dialog is reopened (or the action changes).
  useEffect(() => {
    if (isOpen) {
      setReason('');
      setError(null);
      setSubmitting(false);
    }
  }, [isOpen, action]);

  const handleOpenChange = (open: boolean) => {
    if (!open && !submitting) {
      onClose();
    }
  };

  const handleSubmit = async () => {
    if (!action) return;
    if (reason.trim().length < MIN_REASON_LENGTH) {
      setError(`Reason must be at least ${MIN_REASON_LENGTH} characters.`);
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await onSubmit(reason.trim());
      onClose();
    } catch (e) {
      const message =
        e instanceof Error ? e.message : 'Failed to submit feedback.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  // Render the Dialog so Radix can manage mount/unmount + animations. We still
  // gate the trigger via `isOpen`, and `action` is required to render content.
  const title = action ? ACTION_LABEL[action] : '';
  const description = action ? ACTION_DESCRIPTION[action] : '';

  return (
    <Dialog open={isOpen && action !== null} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className="pt-1">{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <label
            htmlFor="coverage-audit-feedback-reason"
            className="block text-sm font-medium text-gray-700"
          >
            Reason
          </label>
          <Textarea
            id="coverage-audit-feedback-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why is this the right decision?"
            className="min-h-[100px]"
            disabled={submitting}
            autoFocus
          />
          {error && (
            <p className="text-sm text-red-600" role="alert">
              {error}
            </p>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || reason.trim().length < MIN_REASON_LENGTH}
          >
            {submitting ? 'Submitting…' : 'Submit'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FeedbackDialog;
